import { X, Phone, Clock, Calendar, FileText, User } from 'lucide-react';
import { format } from 'date-fns';
import { getBrand } from '../brand';

export default function CallDetails({ call, onClose, displayConfig }) {
  const brand = getBrand();

  const parseContext = (ctx) => {
    if (!ctx) return null;
    if (typeof ctx === 'string') {
      try {
        return JSON.parse(ctx);
      } catch {
        return ctx;
      }
    }
    return ctx;
  };

  const gathered = parseContext(call.gathered_context);
  const initial = parseContext(call.initial_context);

  const formatPulse = (seconds) => {
    if (!seconds) return '0 pulses';
    const pulses = Math.ceil(seconds / 60);
    return `${pulses} pulse${pulses === 1 ? '' : 's'}`;
  };

  const getNestedValue = (obj, path) => {
    return path.split('.').reduce((current, key) => current?.[key], obj);
  };

  const formatFieldValue = (value, type) => {
    if (!value) return 'N/A';
    switch (type) {
      case 'phone':
        const phone = String(value).replace(/\D/g, '');
        return phone.length === 10 ? `+91-${phone.slice(0, 5)}-${phone.slice(5)}` : value;
      case 'currency':
        return `₹${value}`;
      case 'datetime':
      case 'timestamp':
        try {
          const d = new Date(value);
          if (!isNaN(d.getTime())) {
            return d.toLocaleString('en-IN', {
              year: 'numeric',
              month: 'short',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
            });
          }
        } catch (e) {
          return value;
        }
        return value;
      default:
        return String(value);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-surface rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] flex flex-col overflow-hidden">
        <div
          className="px-6 py-4 flex items-center justify-between text-white"
          style={{ backgroundColor: brand.primary }}
        >
          <div className="flex items-center gap-3">
            <Phone className="w-6 h-6" />
            <div>
              <h2 className="text-lg font-semibold">Call Details</h2>
              <p className="text-xs text-white/70">Run ID: {call.workflow_run_id}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-white/10 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="overflow-y-auto p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-100">
              <div className="flex items-center gap-2 text-text-muted mb-1">
                <Calendar className="w-4 h-4" />
                <span className="text-sm font-medium">Date & Time</span>
              </div>
              <p className="text-text-main">{format(new Date(call.created_at), 'PPpp')}</p>
            </div>
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-100">
              <div className="flex items-center gap-2 text-text-muted mb-1">
                <Clock className="w-4 h-4" />
                <span className="text-sm font-medium">Duration</span>
              </div>
              <p className="text-text-main">{formatPulse(call.call_duration_seconds)}</p>
            </div>
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-100">
              <div className="flex items-center gap-2 text-text-muted mb-1">
                <FileText className="w-4 h-4" />
                <span className="text-sm font-medium">Workflow</span>
              </div>
              <p className="text-text-main">{call.workflow_name || `Workflow ${call.workflow_id}`}</p>
            </div>
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-100">
              <div className="flex items-center gap-2 text-text-muted mb-1">
                <User className="w-4 h-4" />
                <span className="text-sm font-medium">Customer</span>
              </div>
              <p className="text-text-main">
                {gathered?.customer_name ||
                  String(gathered?.phone || gathered?.phone_number || initial?.phone || initial?.phone_number || `Call ${call.workflow_run_id?.slice(-6) || call.id}`)}
              </p>
            </div>
          </div>

          {displayConfig && displayConfig.length > 0 && (
            <div className="mb-6">
              <h3 className="text-base font-semibold text-text-main mb-3">Customer Information</h3>
              <div className="bg-slate-50 rounded-lg border border-slate-100 p-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
                  {displayConfig.map((field, index) => {
                    const value = getNestedValue(call, field.field_path);
                    if (!value) return null;
                    return (
                      <div key={index} className="flex items-start gap-3">
                        <span className="text-2xl">{field.icon}</span>
                        <div className="min-w-0">
                          <p className="text-xs text-text-muted">{field.display_label}</p>
                          <p className="text-sm font-medium text-text-main break-words">
                            {formatFieldValue(value, field.field_type)}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {gathered?.conversation_summary && (
            <div>
              <h3 className="text-base font-semibold text-text-main mb-3">Conversation Summary</h3>
              <div className="bg-accent/5 border border-accent/10 rounded-lg p-4">
                <p className="text-sm text-text-main leading-relaxed">{gathered.conversation_summary}</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
